/** @jsxImportSource @emotion/react */

import { memo, Fragment } from 'react';
import { connect } from 'unistore/react';
import { css } from '@emotion/react';
import { parseISO } from 'date-fns';

import { alertColors, LinkItem } from '../common';
import { moneyFormatter, dateFormat } from '@utils/customLib';

// import SmallPulse from '../../Loader/smallPulse';

import { getCoinProps, getBaseProps } from '@utils/params';

export const checkAppropType = (order_id, coinStr, trade_amt, floatPlaces) => {
	const staticText = 'Your wallet balance has been updated';

	const checkType = orderId => order_id.startsWith(orderId);

	switch (true) {
		case checkType('FIP_REDEEM'): {
			return {
				hasIntent: false,
				title: `${trade_amt} ${coinStr} redeemed from ${coinStr} FD`,
				text: staticText,
			};
		}
		case checkType('POOL_AMT_CREDIT'): {
			return {
				hasIntent: false,
				title: `You have won ${trade_amt} ${coinStr} for participating in pool sale!`,
				text: staticText,
			};
		}
		case checkType('DEPOSIT_DROPLET'): {
			return {
				hasIntent: false,
				title: `${trade_amt} ${coinStr}	has been transferred from Bitdroplet`,
				text: staticText,
			};
		}
		case checkType('VOUCH_CLAIMED'): {
			return {
				hasIntent: false,
				title: `${trade_amt} ${coinStr}	voucher successfully claimed`,
				text: staticText,
			};
		}
		case checkType('FIP_MATURE'): {
			return {
				hasIntent: false,
				title: `${coinStr} FD has matured! ${trade_amt} ${coinStr}  Deposited`,
				text: staticText,
			};
		}
		case checkType('BTC_MONTHLY_AD'): {
			return {
				hasIntent: false,
				title: `${trade_amt} ${coinStr} HODL interest credited`,
				text: staticText,
			};
		}
		case checkType('COIN_MONTHLY_AD'): {
			return {
				hasIntent: false,
				title: `${trade_amt} ${coinStr} HODL interest credited`,
				text: staticText,
			};
		}

		case checkType('REFUND_REFERRAL'): {
			return {
				hasIntent: false,
				title: `${trade_amt} ${coinStr} Referral bonus earnt!`,
				text: staticText,
			};
		}
		case checkType('INR_HYPTO'): {
			return {
				hasIntent: false,
				title: `${trade_amt} ${coinStr} reverted back to your ${coinStr} wallet`,
				text: staticText,
			};
		}
		default: {
			return {
				hasIntent: true,
				title: `${coinStr} Deposit`,
				text: `${coinStr} ${moneyFormatter(trade_amt, {
					maximumFractionDigits: floatPlaces,
				})} deposited`,
			};
		}
	}
};

export const getAppropMsg = (
	order_id,
	action,
	coinStr,
	market,
	trade_amt,
	rate,
	type,
	country
) => {
	if (coinStr) {
		const { sym, market_id } = getBaseProps(market);
		const lowerCoin = coinStr.toLowerCase();
		const { coinIcon, floatPlaces } = getCoinProps(coinStr);

		switch (action) {
			case 1: // LIQUIDATE_ZONE
				return {
					alertType: -2,
					title: 'Attention!',
					icon: coinIcon,
					text: `Your ${coinStr} is in Liquidate Zone`,
					links: [
						{
							text: 'See Status',
							link: '/margin-trades/',
							type: 0,
						},
					],
				};
			case 2: // DEPOSIT_ZONE
				return {
					alertType: -1,
					title: 'Attention!',
					icon: coinIcon,
					text: (
						<Fragment>
							Your {coinStr} is in Deposit Zone
							<br />
							Deposit more worth to stay safe
						</Fragment>
					),
					links: [
						{
							text: 'See Status',
							link: '/margin-trades/',
							type: 0,
						},
						{
							text: `Deposit More ${coinStr}`,
							link: `/crypto-deposit/${lowerCoin}`,
							type: 0,
						},
					],
				};
			case 3: {
				// DISPUTE_PROOF_UPLOADED
				const p2pType = type === 0 ? 'Deposit' : 'Withdraw';
				let p2pMode = 'bank';

				switch (lowerCoin) {
					case 'inr':
						p2pMode = 'Bank';
						break;
					case 'usdt':
						p2pMode = 'USDT';
						break;
					case 'bidforx':
					default: // cos i dunno
						p2pMode = 'Bidforx';
						break;
				}

				return {
					alertType: -1,
					title: 'Attention!',
					icon: 'https://bitbns.com/assets/img/appIcons/svg/dispute.svg',
					text: `Your ${p2pMode} ${p2pType} peer has uploaded a dispute proof.`,
					links: [
						{
							text: 'Check Now',
							link: `/wallets/${p2pType.toLowerCase()}/p2p/${p2pMode.toLowerCase()}${country}/order/pending`,
							type: 0,
						},
					],
				};
			}
			case 4: // PURCHASED
				return {
					alertType: 0,
					icon: coinIcon,
					title: `${coinStr}/${market} Trade`,
					text: `${moneyFormatter(trade_amt, {
						maximumFractionDigits: floatPlaces,
					})} ${coinStr} bought at ${sym}${rate}`,
					links: [
						{
							text: 'See Trades',
							link:
								market_id === 0
									? `/${lowerCoin}/`
									: `/${lowerCoin}-${market.toLowerCase()}/`,
							type: 0,
						},
					],
				};
			case 5: // SOLD
				return {
					alertType: 0,
					icon: coinIcon,
					title: `${coinStr}/${market} Trade`,
					text: `${moneyFormatter(trade_amt, {
						maximumFractionDigits: floatPlaces,
					})} ${coinStr} sold at ${sym}${rate}`,
					links: [
						{
							text: 'See Trades',
							link:
								market_id === 0
									? `/${lowerCoin}/`
									: `/${lowerCoin}-${market.toLowerCase()}/`,
							type: 0,
						},
					],
				};
			case 6: // DEPOSITED
				return {
					alertType: 0,
					icon: coinIcon,
					...checkAppropType(
						order_id,
						coinStr,
						trade_amt,
						floatPlaces
					),
					links: [
						{
							text: 'See History',
							link: '/wallets/',
							type: 0,
						},
					],
				};
			default:
				return {};
		}
	}
	return {};
};

const AlertsRow = memo(
	({
		actions: action,
		coinStr,
		market,
		time,
		trade_amt,
		rate,
		type,
		close,
		userAlertsLastRead,
		selectedCountry,
		order_id,
	}) => {
		const { alertType, icon, title, text, links } = getAppropMsg(
			order_id,
			action,
			coinStr,
			market,
			trade_amt,
			rate,
			type,
			selectedCountry
		);

		const notRead = new Date(time).getTime() / 1000 > userAlertsLastRead;

		if (typeof icon === 'undefined' || !action) {
			return null;
		}

		return (
			<li
				className={`bb-notification__notifLi py-8 px-8 flex items-start shadow-inner hover:shadow-none ${
					notRead
						? 'bg-white'
						: 'bg-snowWhite text-lightGray hover:bg-white'
				}`}
			>
				<img
					src={icon}
					alt={title}
					height="28"
					width="28"
					css={css`
						opacity: ${notRead ? 1 : 0.6};
					`}
					className="bg-gray-300 p-2 mr-2 rounded-full"
				/>
				<div className="flex-grow text-sm">
					<div className="relative">
						{/* <div
							className={`rounded-full ${
								alertColors.get(type).bg
							} absolute`}
							style={circleStyle}
						/> */}

						<div className="leading-normal ml-2 flex-grow flex items-start justify-between">
							<span
								className={`font-bold ${
									notRead
										? alertColors.get(alertType).text
										: ''
								}`}
							>
								{title}
							</span>
							<div
								css={css`
									height: 1.6rem;
								`}
								className="text-xs leading-snug text-right whitespace-nowrap"
							>
								{dateFormat(
									parseISO(time),
									'h:mm a',
									action === 6 ? -330 : 0
								)}
								<br />
								{dateFormat(
									parseISO(time),
									'd MMM',
									action === 6 ? -330 : 0
								)}
							</div>
						</div>
						{/* {date > userAlertsLastRead && (
							<SmallPulse
								style={pulseStyle}
								className="text-pastelRed mr-4"
							/>
						)} */}
					</div>
					<p className="leading-normal my-2 ml-2">{text}</p>

					{links.length > 0 && (
						<div className="mt-4 ml-2">
							{links.map(link => (
								<LinkItem
									{...link}
									close={close}
									title={text}
									key={`${link.text}-${link.link}`}
								/>
							))}
						</div>
					)}
				</div>
			</li>
		);
	}
);

export default connect('userAlertsLastRead, selectedCountry')(AlertsRow);
